/**
 * Inline Skill Library view. Lists all firm + visible client skills
 * from Convex; shows frontmatter, body preview, manifest, and a
 * version dropdown for the selected row.
 *
 * Steady-state reads come from Convex (D3). The Sync button calls the
 * Graph wrapper to re-enumerate /Skills/ on the firm site and bumps
 * Convex through skills.upsertFromExternal. Wiring lives in
 * lib/graph/skillsWiring.ts so this component stays React-only.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { Doc, Id } from '../../../convex/_generated/dataModel';
import { requestVaultSelect } from '../obsidian-vault/activeVaultSelection';
import { useWindowContext } from '../desktop/WindowContext';
import { formatTrainedRelative } from './trainedRelativeTime';
import { MarketplaceTab } from './MarketplaceTab';
import { TrainLoraButton } from './TrainLoraButton';
import { BundleSpecializeButton } from './BundleSpecializeButton';

type LibraryTab = 'mine' | 'marketplace';

interface SkillLibraryWindowProps {
  /** Optional sync callback wired by the parent (Settings or Desktop). */
  onSync?: () => void | Promise<void>;
}

// Row shape used in the left list.
type LibrarySkill = {
  _id: string;
  name: string;
  description: string;
  scope: 'firm' | 'client';
  currentVersion: number;
  externalRef?: Doc<'skills'>['externalRef'];
  vaultPath?: string;
};

export function SkillLibraryWindow({ onSync }: SkillLibraryWindowProps) {
  const { openWindow } = useWindowContext();

  const liveSkills = useQuery(api.skills.list, {});

  // T.1 — read the latest Tinker run so we can paint a "✨ Trained"
  // badge on Skills that participated.
  const recentTinkerRuns = useQuery(
    api.tinkerSpikeRuns.listRecent,
    { limit: 1 },
  ) as
    | Array<{ _id: string; status: string; skillNamesUsedJson?: string; completedAt?: number }>
    | undefined;
  const latestCompleteRun = recentTinkerRuns?.find((r) => r.status === 'complete');
  const trainedSkillNames = (() => {
    if (!latestCompleteRun?.skillNamesUsedJson) return new Set<string>();
    try {
      const parsed = JSON.parse(latestCompleteRun.skillNamesUsedJson);
      return new Set<string>(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set<string>();
    }
  })();

  // T.2.e — resolve the registered LoRA URL for the latest complete
  // run via the `lorasByPortal` registry. Falls back to `null` when no
  // registry row exists.
  const trainedLora = useQuery(
    api.lorasByPortal.findBySourceRun,
    latestCompleteRun?._id
      ? { sourceRunRowId: latestCompleteRun._id }
      : 'skip',
  ) as { tinkerLoraUrl: string; baseModel: string; loraRank: number } | null | undefined;

  // Popover state — which skill name is showing its LoRA details (null = none).
  const [loraPopoverFor, setLoraPopoverFor] = useState<string | null>(null);
  // L.6 — multi-select state for bundle specialization. Keyed by skill name
  // because tinkerDataset's `skill-bundle:[...]` scope resolves by name.
  const [selectedForBundle, setSelectedForBundle] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const toggleBundleSelect = (name: string): void => {
    setSelectedForBundle((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const clearBundle = (): void => setSelectedForBundle(new Set());
  const skills: LibrarySkill[] | undefined = liveSkills?.map((s) => ({
    _id: String(s._id),
    name: s.name,
    description: s.description,
    scope: s.scope,
    currentVersion: s.currentVersion,
    externalRef: s.externalRef,
    vaultPath: (s as { vaultPath?: string }).vaultPath,
  }));

  const [activeTab, setActiveTab] = useState<LibraryTab>('mine');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = useQuery(
    api.skills.getWithVersions,
    selectedId ? { skillId: selectedId as Id<'skills'> } : 'skip',
  );
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [syncing, setSyncing] = useState(false);

  // F-NIPE-DESKTOP-AWARENESS — read-only bridge for
  // readWindowContext('/skills'). Reports the currently selected skill
  // (right pane) + any bundle multi-selection (specialization picker).
  const skillBridgeRef = useRef({
    selectedId,
    selectedName: selected?.skill?.name ?? null,
    bundleNames: Array.from(selectedForBundle),
  });
  skillBridgeRef.current = {
    selectedId,
    selectedName: selected?.skill?.name ?? null,
    bundleNames: Array.from(selectedForBundle),
  };
  useEffect(() => {
    window.__repancySkillLibrary = () => ({
      selectedSkillId: skillBridgeRef.current.selectedId,
      selectedSkillName: skillBridgeRef.current.selectedName,
      selectedBundleSkillIds: skillBridgeRef.current.bundleNames,
    });
    return () => {
      delete window.__repancySkillLibrary;
    };
  }, []);

  async function handleSync(): Promise<void> {
    if (!onSync) return;
    setSyncing(true);
    try {
      await onSync();
    } finally {
      setSyncing(false);
    }
  }

  const activeVersion = selected
    ? selected.versions.find((v) => v.versionNumber === (selectedVersion ?? selected.skill.currentVersion))
    : undefined;

  return (
    <div
      data-testid={testIds.skills.libraryRoot}
      className="w-full h-full bg-[#ece9d8] text-black font-[MS_Sans_Serif,Tahoma,Geneva,sans-serif] flex flex-col"
    >
      <div className="flex items-center justify-between p-1 border-b-2 border-solid border-b-[#808080]">
        <span className="font-bold text-[12px]">Skill Library</span>
        <button
          data-testid={testIds.skills.syncButton}
          onClick={handleSync}
          disabled={syncing || !onSync}
          title="Pull the latest SKILL.md files from the firm's /Skills folder in SharePoint into the local Skill Library."
          className="text-[11px] px-2 py-0.5 cursor-pointer bg-[#ece9d8] border-2 border-solid border-t-[#dfdfdf] border-l-[#dfdfdf] border-b-[#808080] border-r-[#808080] disabled:opacity-60"
        >
          {syncing ? 'Syncing…' : 'Sync from SharePoint'}
        </button>
      </div>

      {/* MK.3 — tab strip: My Skills (live + demo) vs Browse Marketplace. */}
      <div
        className="flex items-end gap-0 px-2 pt-2"
        style={{ background: '#ece9d8', borderBottom: '1px solid #aca899' }}
      >
        {([
          { id: 'mine' as const, label: 'My Skills', testId: testIds.skills.libraryTabMine },
          { id: 'marketplace' as const, label: 'Browse Marketplace', testId: testIds.skills.libraryTabMarketplace },
        ]).map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              data-testid={tab.testId}
              onClick={() => setActiveTab(tab.id)}
              className="text-[11px] px-3 py-1 cursor-pointer select-none"
              style={{
                fontFamily: 'var(--xp-font-body)',
                background: active
                  ? 'linear-gradient(180deg, #fff, #ecebe5 86%, #d8d0c4)'
                  : 'linear-gradient(180deg, #f5f1e0, #e0dbc8)',
                border: '1px solid #aca899',
                borderBottom: active ? '1px solid transparent' : '1px solid #aca899',
                borderTopLeftRadius: 4,
                borderTopRightRadius: 4,
                marginBottom: active ? -1 : 0,
                fontWeight: active ? 700 : 400,
                color: '#000',
                position: 'relative',
                zIndex: active ? 2 : 1,
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'marketplace' ? (
        <MarketplaceTab />
      ) : (
      <div className="flex flex-1 overflow-hidden">
        <div className="w-[260px] border-r-2 border-solid border-r-[#808080] flex flex-col">
        <div className="flex-1 overflow-y-auto p-1">
          {skills === undefined && (
            <div className="text-[11px] text-gray-800 italic px-2 py-3">Loading…</div>
          )}
          {skills && skills.length === 0 && (
            <div
              data-testid={testIds.skills.libraryEmpty}
              className="text-[11px] text-gray-800 italic px-2 py-3"
            >
              No skills yet. Click <strong>Sync from SharePoint</strong> to pull
              from /Skills/, or use <strong>Save as Skill…</strong> on a working
              Prompt Lab session.
            </div>
          )}
          {skills?.map((s) => (
            <div
              key={s._id}
              role="button"
              tabIndex={0}
              data-testid={testIds.skills.libraryRow(s._id)}
              onClick={() => {
                setSelectedId(s._id);
                setSelectedVersion(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedId(s._id);
                  setSelectedVersion(null);
                }
              }}
              className={`flex items-start gap-1.5 px-2 py-1.5 text-black cursor-pointer ${
                selectedId === s._id
                  ? 'bg-[#316ac5] text-white'
                  : 'hover:bg-[#e8e8e0]'
              }`}
              style={{
                borderBottom: '1px solid #d5d2c8',
              }}
            >
              <input
                type="checkbox"
                data-testid={testIds.skills.libraryRowSelectCheckbox(s._id)}
                checked={selectedForBundle.has(s.name)}
                onChange={(e) => { e.stopPropagation(); toggleBundleSelect(s.name); }}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${s.name} for bundle specialization`}
                className="mt-0.5 cursor-pointer"
              />
              <div className="flex-1 min-w-0">
              <div className="font-bold text-[11px] flex items-center gap-1">
                <span>{s.name}</span>
                {trainedSkillNames.has(s.name) && (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={testIds.skills.libraryRowTrainedBadge(s.name)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setLoraPopoverFor(
                        loraPopoverFor === s.name ? null : s.name,
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        setLoraPopoverFor(
                          loraPopoverFor === s.name ? null : s.name,
                        );
                      }
                    }}
                    title={`Model is specialized on this skill${
                      latestCompleteRun?.completedAt
                        ? ` since ${new Date(latestCompleteRun.completedAt).toLocaleDateString()}`
                        : ''
                    }${trainedLora ? ' — click for details' : ''}`}
                    className="inline-block text-[9px] text-green-900 bg-green-100 border border-green-700 px-1 leading-[1.1] rounded-sm cursor-pointer hover:bg-green-200"
                  >
                    ✨ Specialized
                  </span>
                )}
                {loraPopoverFor === s.name && trainedLora && (
                  <div
                    data-testid={testIds.skills.libraryRowTrainedLoraPopover}
                    className="absolute z-10 mt-1 ml-2 max-w-[280px] bg-[#ece9d8] border border-[#7f9db9] p-2 text-[10px] text-black shadow-[2px_2px_0_0_#404040]"
                  >
                    <div className="font-bold mb-1">Specialization registered</div>
                    {latestCompleteRun?.completedAt && (
                      <div
                        data-testid={testIds.skills.libraryRowTrainedRelativeTime}
                        className="text-black mb-1"
                      >
                        {formatTrainedRelative(latestCompleteRun.completedAt)}
                      </div>
                    )}
                    <div>
                      <span className="font-bold">Base:</span> {trainedLora.baseModel}
                    </div>
                    <div>
                      <span className="font-bold">Rank:</span> {trainedLora.loraRank}
                    </div>
                    <div className="mt-1">
                      <span className="font-bold">Run ID:</span>{' '}
                      <code
                        data-testid={testIds.skills.libraryRowTrainedRunId}
                        className="text-[9px] text-black break-all"
                      >
                        {latestCompleteRun?._id ?? ''}
                      </code>
                    </div>
                    <div className="mt-1 break-all">
                      <span className="font-bold">URL:</span>{' '}
                      <code
                        data-testid={testIds.skills.libraryRowTrainedLoraUrl}
                        className="text-[9px] text-blue-900"
                      >
                        {trainedLora.tinkerLoraUrl}
                      </code>
                    </div>
                    <div className="mt-1">
                      <span
                        role="button"
                        tabIndex={0}
                        data-testid={testIds.skills.libraryRowTrainedViewAllLink}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setLoraPopoverFor(null);
                          openWindow('/settings');
                        }}
                        onKeyDown={(ev) => {
                          if (ev.key === 'Enter' || ev.key === ' ') {
                            ev.stopPropagation();
                            setLoraPopoverFor(null);
                            openWindow('/settings');
                          }
                        }}
                        className="text-[#0000ee] underline bg-transparent border-0 p-0 cursor-pointer text-[10px]"
                      >
                        View all specialization runs →
                      </span>
                    </div>
                  </div>
                )}
              </div>
              <div className={`text-[10px] ${selectedId === s._id ? 'text-blue-100' : 'text-gray-600'}`}>
                {s.scope === 'firm' ? 'Firm' : 'Client'} · v{s.currentVersion}
              </div>
              </div>
            </div>
          ))}
        </div>
        <BundleSpecializeButton
          selectedNames={Array.from(selectedForBundle)}
          onClear={clearBundle}
        />
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {!selected && (
            <div className="text-[11px] text-gray-800 italic">
              Select a skill on the left to preview.
            </div>
          )}
          {selected && (
            <div className="space-y-2">
              <div>
                <div className="font-bold text-[12px]">{selected.skill.name}</div>
                <div className="text-[11px] text-black">{selected.skill.description}</div>
              </div>
              {/* MK.4 — train-a-LoRA from this skill. */}
              <TrainLoraButton
                skillId={String(selected.skill._id)}
                skillName={selected.skill.name}
              />
              <div className="flex items-center gap-2">
                <span className="text-[11px]">Version:</span>
                <select
                  data-testid={testIds.skills.skillVersionSelect}
                  value={selectedVersion ?? selected.skill.currentVersion}
                  onChange={(e) => setSelectedVersion(Number(e.target.value))}
                  className="text-[11px] bg-white text-black border border-solid border-gray-600 px-1 py-0.5"
                >
                  {selected.versions.map((v) => (
                    <option key={v.versionNumber} value={v.versionNumber}>
                      v{v.versionNumber}
                      {v.versionNumber === selected.skill.currentVersion ? ' (current)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <pre
                data-testid={testIds.skills.skillBodyPreview}
                className="bg-white text-black text-[11px] p-1 whitespace-pre-wrap break-words font-mono"
                style={{
                  borderStyle: 'solid',
                  borderWidth: 2,
                }}
              >
                {activeVersion?.body ?? ''}
              </pre>
              {activeVersion && activeVersion.scriptManifest.length > 0 && (
                <div>
                  <div className="font-bold text-[11px]">Manifest</div>
                  <ul className="text-[10px] text-black">
                    {activeVersion.scriptManifest.map((m) => (
                      <li key={m.path}>
                        {m.kind} · {m.path} · sha256 {m.sha256.slice(0, 12)}…
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selected.skill.externalRef && (
                <div className="text-[10px]">
                  <a
                    href={selected.skill.externalRef.webUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="text-[#0000ee] underline"
                  >
                    Open in SharePoint
                  </a>
                </div>
              )}
              {(selected.skill as { vaultPath?: string }).vaultPath && (
                <div className="text-[10px]">
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={testIds.skills.openInVault(String(selected.skill._id))}
                    onClick={() => {
                      const path = (selected.skill as { vaultPath?: string }).vaultPath!;
                      requestVaultSelect(path);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        const path = (selected.skill as { vaultPath?: string }).vaultPath!;
                        requestVaultSelect(path);
                      }
                    }}
                    className="text-[#0000ee] underline bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Open in vault
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}
